// src/components/copiloto/CopilotoAcao.js — o card de confirmação do Copiloto.
//
// Out/26 (Cetto Copiloto, fase 3). A regra do produto: nada que envie
// mensagem é gravado sem o clique da pessoa. A IA só monta este card; o
// "Confirmar agendamento" é a ÚNICA coisa que grava, e o servidor refaz
// todas as conferências nesse clique (services/copiloto_acoes).
//
// O que o card garante na tela:
//   - a contagem REAL de contatos em destaque — "142 contatos", nunca só
//     "a pasta LeadsAds";
//   - acima de 50 contatos, um segundo passo explícito ("Sim, agendar
//     para 142") — o servidor exige o mesmo, a tela só pergunta antes;
//   - se a pasta mudou entre o card e o clique, o servidor NÃO agenda e
//     devolve o card com o número novo: a pessoa confirma o que vê;
//   - clique duplo devolve o mesmo agendamento (o botão também trava);
//   - a caixa de opt-in (blindagem contra banimento, out/26): sem ela o
//     botão não liga, e o servidor recusa com 428 do mesmo jeito. Quantos
//     nunca conversaram com a empresa já vem nos avisos do card.
import { useState } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FiCalendar, FiCheck } from 'react-icons/fi';
import api from '../../services/Api';
import { C, FONT_NUM, FONT_TXT, ACENTO, T } from './tema';

const AMBAR = T.ambar;

// [rótulo, cor, fundo, borda] do selo no canto do card.
const SELOS = {
  pending: ['Aguardando você', AMBAR, T.ambarBg, T.ambarB],
  confirming: ['Agendando', AMBAR, T.ambarBg, T.ambarB],
  executed: ['Confirmado', C.green, T.verdeBg, T.verdeB('rgba(0,214,124,0.28)')],
  cancelled: ['Cancelado', C.text2, T.neutroBg('rgba(255,255,255,0.04)'), T.neutroB],
  expired: ['Expirado', C.text2, T.neutroBg('rgba(255,255,255,0.04)'), T.neutroB],
  failed: ['Não agendado', C.red, T.vermelhoBg('rgba(255,69,96,0.10)'), T.vermelhoB('rgba(255,69,96,0.28)')],
};

const focoVisivel = { '&:focus-visible': { outline: `2px solid ${ACENTO.base}`, outlineOffset: 2 } };

const botaoSecundario = {
  fontFamily: FONT_TXT, fontSize: '11.2px', fontWeight: 600, color: C.text2, bgcolor: 'transparent',
  border: `1px solid ${C.border}`, borderRadius: '6px', p: '6.4px 12px', cursor: 'pointer',
  transition: 'all .15s', '&:hover': { color: C.text, bgcolor: T.hover },
  '&:disabled': { opacity: 0.5, cursor: 'default' }, ...focoVisivel,
};

const botaoPrincipal = {
  display: 'flex', alignItems: 'center', gap: '7px', fontFamily: FONT_TXT, fontSize: '11.2px',
  fontWeight: 700, color: T.sobreVerde, bgcolor: C.green, border: 'none', borderRadius: '6px',
  p: '7.4px 13px', cursor: 'pointer', letterSpacing: '0.02em', transition: 'background .15s',
  '&:hover': { bgcolor: T.verdeHover }, '&:disabled': { cursor: 'default', opacity: 0.85 }, ...focoVisivel,
};

const Girando = () => (
  <Box component="span" aria-hidden="true" sx={{
    width: 11, height: 11, borderRadius: '50%', border: '2px solid rgba(6,13,20,0.25)',
    borderTopColor: T.sobreVerde, animation: 'cpSpin .7s linear infinite',
    '@keyframes cpSpin': { to: { transform: 'rotate(360deg)' } },
  }} />
);

function Linha({ k, big, v, sub, ultima }) {
  return (
    <Box sx={{
      display: 'grid', gridTemplateColumns: { xs: '84px minmax(0,1fr)', sm: '108px minmax(0,1fr)' },
      gap: '12px', alignItems: 'baseline', py: '9px',
      borderBottom: ultima ? 'none' : `1px solid ${T.linha('rgba(255,255,255,0.05)')}`,
    }}>
      <Box component="span" sx={{ fontSize: '9.92px', color: C.text3, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{k}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px', flexWrap: 'wrap' }}>
          {big != null && (
            <Box component="span" sx={{ fontFamily: FONT_NUM, fontSize: '22.4px', fontWeight: 700, color: C.text, lineHeight: 1, letterSpacing: '-0.5px' }}>{big}</Box>
          )}
          <Box component="span" sx={{ fontSize: '12.48px', fontWeight: 600, color: C.text, overflowWrap: 'anywhere' }}>{v}</Box>
        </Box>
        {sub && <Box sx={{ fontSize: '10.56px', color: C.text2, mt: '2px' }}>{sub}</Box>}
      </Box>
    </Box>
  );
}

function Faixa({ cor, fundo, borda, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'stretch', borderTop: `1px solid ${borda}`, background: `linear-gradient(90deg, ${fundo} 0%, transparent 58%)`, animation: 'cpIn .25s ease' }}>
      <Box sx={{ width: '2px', bgcolor: cor, flexShrink: 0 }} />
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', flexWrap: 'wrap' }}>{children}</Box>
    </Box>
  );
}

export default function CopilotoAcao({ acao: inicial }) {
  const navigate = useNavigate();
  const [acao, setAcao] = useState(inicial);
  const [etapa, setEtapa] = useState(''); // '' | 'segunda'
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState('');
  const [optin, setOptin] = useState(false);

  const n = acao.destino?.contatos ?? 0;
  const status = ocupado ? 'confirming' : acao.status;
  const [rotulo, cor, fundo, borda] = SELOS[status] || SELOS.pending;
  const pendente = acao.status === 'pending';

  const confirmar = async (segunda) => {
    if (ocupado || !optin) return;
    if (acao.precisa_segunda_confirmacao && !segunda) { setEtapa('segunda'); return; }
    setOcupado(true);
    setAviso('');
    try {
      const { data } = await api.post(`/copilot/actions/${acao.acao_id}/confirm`, {
        contatos_vistos: n, segunda_confirmacao: !!segunda, optin_confirmado: optin,
      });
      setAcao(data.acao);
      setEtapa('');
    } catch (e) {
      const d = e?.response?.data;
      if (d?.acao) setAcao(d.acao);
      setEtapa(d?.motivo === 'segunda_confirmacao' ? 'segunda' : '');
      setAviso(d?.detail || (e?.response ? 'Não foi possível agendar.' : 'Sem conexão com o servidor. Nada foi agendado.'));
    } finally {
      setOcupado(false);
    }
  };

  const cancelar = async () => {
    if (ocupado) return;
    setOcupado(true);
    try {
      const { data } = await api.post(`/copilot/actions/${acao.acao_id}/cancel`);
      setAcao(data.acao);
      setEtapa('');
      setAviso('');
    } catch (e) {
      const d = e?.response?.data;
      if (d?.acao) setAcao(d.acao);
      setAviso(d?.detail || 'Não foi possível cancelar.');
    } finally {
      setOcupado(false);
    }
  };

  const destinoSub = [
    acao.destino?.rotulo,
    acao.destino?.sem_valor ? `${acao.destino.sem_valor} sem o dado da variável` : '',
  ].filter(Boolean).join(' · ');

  return (
    <Box
      role="group"
      aria-label="Agendar disparo de template"
      sx={{
        maxWidth: 560, bgcolor: C.bg, border: `1px solid ${T.cardBorda}`, borderRadius: '10px',
        overflow: 'hidden', opacity: ['cancelled', 'expired'].includes(acao.status) ? 0.6 : 1, transition: 'opacity .2s',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', p: '12px 14px', borderBottom: `1px solid ${T.linha('rgba(255,255,255,0.06)')}` }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: '7px', bgcolor: T.verdeBg, border: `1px solid ${T.verdeB('rgba(0,214,124,0.22)')}`,
          color: C.green, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <FiCalendar size={14} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ fontSize: '13.12px', fontWeight: 700, color: C.text }}>Agendar disparo de template</Box>
          <Box sx={{ fontSize: '10.56px', color: C.text2 }}>Revise os dados que o Copiloto entendeu</Box>
        </Box>
        <Box component="span" sx={{
          fontSize: '9.28px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', p: '3px 8px',
          borderRadius: '20px', color: cor, bgcolor: fundo, border: `1px solid ${borda}`, whiteSpace: 'nowrap',
        }}>{rotulo}</Box>
      </Box>

      <Box sx={{ px: '14px', py: '2px' }}>
        <Linha k="Template" v={acao.template?.nome} sub={acao.template?.sub} />
        <Linha k="Destino" big={n.toLocaleString('pt-BR')} v={n === 1 ? 'contato' : 'contatos'} sub={destinoSub} />
        <Linha k="Quando" v={acao.quando?.rotulo} sub={acao.quando?.sub} />
        <Linha k="Chip" v={acao.chip?.nome} sub={acao.chip?.sub} ultima />
      </Box>

      {(acao.avisos || []).length > 0 && pendente && (
        <Box sx={{ px: '14px', pb: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {acao.avisos.map((a) => (
            <Box key={a} sx={{ display: 'flex', gap: '6px', fontSize: '10.88px', color: AMBAR, lineHeight: 1.45 }}>
              <Box component="span" aria-hidden="true">•</Box>{a}
            </Box>
          ))}
        </Box>
      )}

      {pendente && aviso && (
        <Box role="alert" sx={{ px: '14px', pb: '10px', fontSize: '10.88px', color: AMBAR, lineHeight: 1.45 }}>{aviso}</Box>
      )}

      {pendente && (
        <Box
          component="label"
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: '8px', p: '10px 14px', cursor: 'pointer',
            borderTop: `1px solid ${T.linha('rgba(255,255,255,0.06)')}`, fontSize: '11.2px', color: C.text, lineHeight: 1.45,
          }}
        >
          <Box
            component="input" type="checkbox" checked={optin} disabled={ocupado}
            onChange={(e) => setOptin(e.target.checked)}
            aria-label="Confirmo que estes contatos autorizaram receber mensagens"
            sx={{ mt: '2px', accentColor: C.green, cursor: 'pointer', flexShrink: 0, ...focoVisivel }}
          />
          <span>Confirmo que estes contatos autorizaram receber mensagens da minha empresa no WhatsApp.</span>
        </Box>
      )}

      {pendente && etapa !== 'segunda' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: '10px 14px', borderTop: `1px solid ${T.linha('rgba(255,255,255,0.06)')}`, bgcolor: T.rodape, flexWrap: 'wrap' }}>
          <Box component="span" sx={{ flex: { xs: '1 1 100%', sm: 1 }, fontSize: '10.56px', color: C.text2 }}>Nada é enviado antes da sua confirmação.</Box>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Box component="button" type="button" onClick={cancelar} disabled={ocupado} sx={botaoSecundario}>Cancelar</Box>
            <Box component="button" type="button" onClick={() => confirmar(false)} disabled={ocupado || !optin} sx={{ ...botaoPrincipal, '&:disabled': { cursor: 'default', opacity: ocupado ? 0.85 : 0.45 } }}>
              {ocupado && <Girando />}{ocupado ? 'Agendando…' : 'Confirmar agendamento'}
            </Box>
          </Box>
        </Box>
      )}

      {pendente && etapa === 'segunda' && (
        <Faixa cor={AMBAR} fundo={T.ambarBg} borda={T.ambarB}>
          <Box component="span" sx={{ flex: { xs: '1 1 100%', sm: 1 }, fontSize: '11.52px', color: C.text, lineHeight: 1.45 }}>
            São <b>{n.toLocaleString('pt-BR')} contatos</b>. Confirma o agendamento para todos eles?
          </Box>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Box component="button" type="button" onClick={() => { setEtapa(''); setAviso(''); }} disabled={ocupado} sx={botaoSecundario}>Voltar</Box>
            <Box component="button" type="button" onClick={() => confirmar(true)} disabled={ocupado || !optin} sx={{ ...botaoPrincipal, '&:disabled': { cursor: 'default', opacity: ocupado ? 0.85 : 0.45 } }}>
              {ocupado && <Girando />}{ocupado ? 'Agendando…' : `Sim, agendar para ${n.toLocaleString('pt-BR')}`}
            </Box>
          </Box>
        </Faixa>
      )}

      {acao.status === 'executed' && (
        <Faixa cor={C.green} fundo={T.verdeBg} borda={T.verdeB('rgba(0,214,124,0.2)')}>
          <FiCheck size={14} color={C.green} />
          <Box component="span" sx={{ flex: 1, minWidth: 160, fontSize: '11.52px', color: C.text }}>
            Agendamento criado para {acao.resultado?.quando_rotulo || acao.quando?.rotulo}.
          </Box>
          <Box
            component="button" type="button" onClick={() => navigate('/contatos?lembretes=1')}
            sx={{ fontFamily: FONT_TXT, fontSize: '11.2px', fontWeight: 700, color: C.green, bgcolor: 'transparent', border: 'none', p: 0, cursor: 'pointer', whiteSpace: 'nowrap', ...focoVisivel }}
          >
            Ver agendamento →
          </Box>
        </Faixa>
      )}

      {acao.status === 'cancelled' && (
        <Box sx={{ p: '10px 14px', borderTop: `1px solid ${T.linha('rgba(255,255,255,0.06)')}`, fontSize: '11.2px', color: C.text2 }}>
          {acao.erro ? `${acao.erro} Nada foi agendado.` : 'Cancelado. Nada foi agendado.'}
        </Box>
      )}

      {acao.status === 'expired' && (
        <Box sx={{ p: '10px 14px', borderTop: `1px solid ${T.linha('rgba(255,255,255,0.06)')}`, fontSize: '11.2px', color: C.text2 }}>
          Este card expirou sem confirmação. Peça de novo ao Copiloto para montar um atualizado.
        </Box>
      )}

      {acao.status === 'failed' && (
        <Faixa cor={C.red} fundo={T.vermelhoBg('rgba(255,69,96,0.10)')} borda={T.vermelhoB('rgba(255,69,96,0.22)')}>
          <Box component="span" role="alert" sx={{ flex: 1, fontSize: '11.2px', color: C.text, lineHeight: 1.5 }}>
            {acao.erro || 'Não foi possível agendar.'} Nada foi agendado — peça de novo ao Copiloto.
          </Box>
        </Faixa>
      )}
    </Box>
  );
}
